import { useMemo, useState } from "react";
import type { Rec } from "@shared/types.js";
import { fnum } from "../lib/format.js";

interface Col {
  key: string;
  label: string;
  cls?: string;
}

const TABLE_COLS: Col[] = [
  { key: "compLabel", label: "Component" },
  { key: "code", label: "Code", cls: "n" },
  { key: "name", label: "Supplier" },
  { key: "product", label: "Product" },
  { key: "town", label: "Town" },
  { key: "region", label: "Region" },
  { key: "cat", label: "Cat" },
  { key: "_recv", label: "CR received" },
  { key: "_nr", label: "New rate" },
  { key: "_est", label: "Estimation" },
  { key: "rateType", label: "Rate type" },
  { key: "nrPic", label: "PIC (new rate)" },
  { key: "estPic", label: "PIC (est.)" },
  { key: "ccBy", label: "Crosschecked by" },
  { key: "ccDate", label: "Crosscheck date" },
  { key: "exDate", label: "Export date" },
];

function cellValue(r: Rec, key: string): string {
  switch (key) {
    case "_recv": return r.received ? "Received" : "Not received";
    case "_nr": return r.nrDone ? "Done" : r.received ? "Awaiting" : "—";
    case "_est": return r.estDone ? "Done" : !r.received ? "Outstanding" : "—";
    default: {
      const v = (r as unknown as Record<string, unknown>)[key];
      return v === null || v === undefined || v === false ? "" : String(v);
    }
  }
}

export function DataTable({
  rows, onExport, onOpenRecord,
}: {
  rows: Rec[];
  onExport: (kind: "csv" | "copy") => void;
  onOpenRecord: (r: Rec) => void;
}) {
  const [sortKey, setSortKey] = useState("name");
  const [dir, setDir] = useState(1);

  const sorted = useMemo(() => {
    const copy = rows.slice();
    copy.sort((a, b) => {
      if (sortKey === "code") {
        const x = parseInt(cellValue(a, "code"), 10) || 0;
        const y = parseInt(cellValue(b, "code"), 10) || 0;
        return (x < y ? -1 : x > y ? 1 : 0) * dir;
      }
      const x = cellValue(a, sortKey);
      const y = cellValue(b, sortKey);
      return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
    return copy;
  }, [rows, sortKey, dir]);

  const toggleSort = (key: string) => {
    if (key === sortKey) setDir((d) => -d);
    else { setSortKey(key); setDir(1); }
  };

  return (
    <div className="sec">
      <div className="sec-head">
        <span className="eyebrow">Section 6</span>
        <h2><i className="bi bi-table hico" aria-hidden="true" />Detailed data</h2>
        <span className="note">Every filter above applies &middot; click a column to sort</span>
      </div>
      <div className="card">
        <div className="card-head">
          <h3><i className="bi bi-table hico" aria-hidden="true" />{fnum(rows.length)} record{rows.length === 1 ? "" : "s"}</h3>
          <span className="sub">all shown &middot; click a row for the full record</span>
        </div>
        <div className="tablewrap" style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <table>
            <thead>
              <tr>
                {TABLE_COLS.map((c) => (
                  <th
                    key={c.key}
                    className={`sortable ${c.cls ?? ""}`}
                    onClick={() => toggleSort(c.key)}
                    aria-sort={sortKey === c.key ? (dir === 1 ? "ascending" : "descending") : "none"}
                  >
                    {c.label}
                    <span className="arw">{sortKey === c.key ? (dir === 1 ? "▲" : "▼") : ""}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={TABLE_COLS.length}>
                    <div className="empty">No records match the current filters.</div>
                  </td>
                </tr>
              )}
              {sorted.map((r) => (
                <tr
                  key={r.uid} style={{ cursor: "pointer" }}
                  onClick={() => onOpenRecord(r)}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") onOpenRecord(r); }}
                >
                  {TABLE_COLS.map((c) => {
                    const v = cellValue(r, c.key);
                    if (c.key === "_recv") {
                      return <td key={c.key}><span className={`pill ${r.received ? "p-done" : "p-none"}`}>{v}</span></td>;
                    }
                    if (c.key === "_nr") {
                      const cls = r.nrDone ? "p-done" : r.received ? "p-await" : "p-none";
                      return <td key={c.key}><span className={`pill ${cls}`}>{v}</span></td>;
                    }
                    if (c.key === "_est") {
                      return <td key={c.key}><span className={`pill ${r.estDone ? "p-est" : "p-none"}`}>{v}</span></td>;
                    }
                    if (c.key === "cat") {
                      return (
                        <td key={c.key}>
                          {v
                            ? <span className="catbadge">{v}</span>
                            : <span className="catbadge" title="missing">&mdash;</span>}
                        </td>
                      );
                    }
                    return <td key={c.key} className={c.cls ?? ""}>{v}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="tblnote" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ flex: 1 }}>
            Export respects the current filters and includes every field, not just the columns shown.
          </span>
          <button className="btn btn--sm btn--primary" type="button" onClick={() => onExport("csv")}>
            Download CSV
          </button>
          <button className="btn btn--sm" type="button" onClick={() => onExport("copy")}>
            Copy for Excel
          </button>
        </div>
      </div>
    </div>
  );
}
